import {
  afterRenderEffect,
  ChangeDetectionStrategy,
  Component,
  computed,
  ElementRef,
  input,
  model,
  signal,
  viewChild,
  viewChildren,
} from '@angular/core';
import { TranslatePipe } from '@ngx-translate/core';
import type { FormValueControl } from '@angular/forms/signals';
import { UiFieldControl } from '../../../../basics/ui-field-control';
import { SmartFilterChipUIComponent } from '../smart-filter-chip/smart-filter-chip.component';
import { IconsInitConfigType } from '../../../../constants';
import { SmartFilterChipColorType } from '../../../../types';

export interface SmartFilterOption<T extends string = string> {
  value: T;
  title: string;
  icon: IconsInitConfigType;
  color: SmartFilterChipColorType;
}

export const SmartFilterOptionsLayout = { Wrap: 'wrap', Grid: 'grid' } as const;
export type SmartFilterOptionsLayout =
  (typeof SmartFilterOptionsLayout)[keyof typeof SmartFilterOptionsLayout];

@Component({
  selector: 'fr8bot-ui-smart-filter-options-field',
  imports: [TranslatePipe, SmartFilterChipUIComponent],
  templateUrl: './smart-filter-options-field.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SmartFilterOptionsFieldUIComponent
  extends UiFieldControl
  implements FormValueControl<string[] | null>
{
  readonly value = model<string[] | null>([]);
  readonly config = input<SmartFilterOption[]>([]);
  /**
   * `wrap`: chips as wide as their text, wrapping; `grid`: two full-width columns (the filters
   * dialog), or one when a label would not fit on one line in half the width.
   */
  readonly layout = input<SmartFilterOptionsLayout>(SmartFilterOptionsLayout.Wrap);

  protected readonly selected = computed(() => this.value() ?? []);

  private readonly list = viewChild.required<ElementRef<HTMLElement>>('list');
  private readonly chips = viewChildren(SmartFilterChipUIComponent);
  private readonly singleColumn = signal(false);

  protected readonly listClass = computed(() => {
    if (this.layout() === SmartFilterOptionsLayout.Wrap) {
      return 'tw:flex tw:flex-wrap';
    }

    return this.singleColumn() ? 'tw:grid tw:grid-cols-1' : 'tw:grid tw:grid-cols-2';
  });

  constructor() {
    super();

    afterRenderEffect((onCleanup) => {
      if (this.layout() !== SmartFilterOptionsLayout.Grid) {
        return;
      }

      const list = this.list().nativeElement;
      const chips = this.chips();
      const observer = new ResizeObserver(() => {
        const half = (list.clientWidth - (parseFloat(getComputedStyle(list).columnGap) || 0)) / 2;

        this.singleColumn.set(chips.some((chip) => chip.naturalWidth() > half));
      });

      observer.observe(list);
      onCleanup(() => observer.disconnect());
    });
  }

  protected onItemClick(item: string): void {
    const selected = this.selected();

    this.value.set(
      selected.includes(item) ? selected.filter((value) => value !== item) : [...selected, item],
    );
  }
}
